import classNames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import classes from "./tile.module.scss";
import { ArrowDownIcon, ArrowUpIcon, InfoIcon } from "@assets/icons";
import { Stack, IconButton, useIsMobile } from "@altimateai/lego";
import { Typography } from "@altimateai/lego";

interface Props {
  color?: string;
  children?: ReactNode;
  title: string;
  value: string | ReactNode;
  onClick?: () => void;
  helpText?: string;
  className?: string;
  /** Opt in to collapsing the nested breakdown on phone-width viewports.
   *  Off by default: this component is shared with call sites that have
   *  always rendered expanded at every width, and flipping that globally
   *  would collapse their breakdowns without those pages asking for it. */
  collapseOnMobile?: boolean;
}
const ColorTile = ({
  color = "#ffffff",
  children,
  title,
  value,
  onClick,
  helpText,
  className,
  collapseOnMobile = false,
}: Props) => {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(true);
  // On a phone a tile's nested breakdown is a tall wall of boxes the user
  // has to scroll past to reach the rest of the page, so an opted-in tile
  // opens collapsed there and expanded on desktop. Re-runs only when the
  // breakpoint is crossed, so a manual toggle within one layout sticks.
  useEffect(() => {
    if (!collapseOnMobile) return;
    setIsExpanded(!isMobile);
  }, [isMobile, collapseOnMobile]);
  const shouldClick = !!children;
  const handleClick = () => {
    onClick?.();
    setIsExpanded(prev => !prev);
  };

  // Determine if background is light (white or near-white)
  // For nested tiles, SCSS will handle the color inheritance
  const colorLower = color.toLowerCase();
  const isLightBackground =
    colorLower === "#ffffff" ||
    colorLower === "#fff" ||
    colorLower === "white" ||
    !color ||
    color === "";

  return (
    <div
      className={classNames(classes.tile, className, "al-w-full", {
        "cursor-pointer": shouldClick,
        [classes.visible]: isExpanded,
      })}
      style={{ background: color }}
      onClick={handleClick}
    >
      <div className={classes.title}>
        <Typography
          variant="body"
          size="lg"
          weight="medium"
          className={isLightBackground ? "" : "!al-text-white"}
        >
          {title}
        </Typography>

        <Stack>
          {helpText && (
            <IconButton
              variant="ghost"
              className="!al-p-0 al-w-fit hover:al-bg-transparent"
              title={helpText}
            >
              <InfoIcon />
            </IconButton>
          )}
          {shouldClick && (
            <div className={classes.expand_btn}>
              <IconButton
                variant="ghost"
                title={isExpanded ? "Collapse" : "Expand"}
                className="!al-p-0 al-w-fit hover:al-bg-transparent"
              >
                {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
              </IconButton>
            </div>
          )}
        </Stack>
      </div>
      <div className={classes.value}>
        {typeof value === "string" ? (
          <Typography
            variant="body"
            size="lg"
            weight="medium"
            className={isLightBackground ? "" : "!al-text-white"}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </div>

      {children ? (
        <div className={classes.children} style={{ color }}>
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default ColorTile;
